import { useState, useEffect } from 'react';
import { RefreshCw, Download, Grid3x3, List, Eye, Power, Wrench, Monitor, CheckCircle, AlertTriangle, XCircle, ServerOff } from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import DataTable from '../../components/common/DataTable';
import type { Column } from '../../components/common/DataTable';
import StatusBadge from '../../components/common/StatusBadge';
import type { DeviceStatus as StatusType } from '../../types';
import StatCard from '../../components/common/StatCard';
import { edgeService, observabilityService } from '../../services';
import type { Edge } from '../../services/edge.service';

interface DeviceWithTelemetry {
  id: string;
  name: string;
  type: string;
  location: string;
  status: StatusType;
  cpu: string;
  memory: string;
  uptime: string;
  lastSeen: string;
  serialNumber: string;
  configStatus: 'synced' | 'pending' | 'drift';
  pendingCount?: number;
  driftCount?: number;
  lastHeartbeat?: string;
}

const DeviceStatus = () => {
  const navigate = useNavigate();
  const [devices, setDevices] = useState<DeviceWithTelemetry[]>([]);
  const [loading, setLoading] = useState(false);
  const [viewMode, setViewMode] = useState<'table' | 'grid'>('table');
  const [statusFilter, setStatusFilter] = useState<'All' | StatusType>('Online');
  const [autoRefresh, setAutoRefresh] = useState<'off' | '30s' | '1m' | '5m'>('30s');

  // Fetch edges and telemetry data
  const fetchDevices = async (isBackground = false) => {
    try {
      if (!isBackground) setLoading(true);
      const options = isBackground ? { showLoading: false } : undefined;
      const [edgesResponse, telemetryResponse] = await Promise.all([
        edgeService.list({ page: 1, pageSize: 100 }, options),
        observabilityService.getEdgesTelemetry(options),
      ]);

      const telemetryData = telemetryResponse.status === 'success' ? telemetryResponse.data : {};

      const mappedDevices: DeviceWithTelemetry[] = edgesResponse.items.map((edge: Edge) => {
        const stats = telemetryData[edge.devSerNum.toLowerCase()] as any;
        const isOnline = stats && stats.cpu !== undefined;
        
        // Format uptime
        let uptimeStr = '-';
        if (stats && stats.uptime !== undefined) {
          const seconds = stats.uptime;
          const days = Math.floor(seconds / 86400);
          const hours = Math.floor((seconds % 86400) / 3600);
          const minutes = Math.floor((seconds % 3600) / 60);
          uptimeStr = `${days}d ${hours}h ${minutes}m`;
        }

        // Determine status based on CPU/Memory thresholds
        let status: StatusType = isOnline ? 'Online' : 'Offline';
        if (isOnline) {
          const cpuVal = stats.cpu || 0;
          const memVal = stats.memory || 0;
          if (cpuVal > 90 || memVal > 90) {
            status = 'Error';
          } else if (cpuVal > 75 || memVal > 75) {
            status = 'Warning';
          }
        }

        // Determine config status
        const configStatus: 'synced' | 'pending' | 'drift' =
          stats && stats.drift_count > 0 ? 'drift' :
          stats && stats.pending_count > 0 ? 'pending' : 'synced';

        return {
          id: edge.id,
          name: edge.devName,
          type: edge.devModel || 'Edge Device',
          location: edge.devLocation || '-',
          status,
          cpu: stats && stats.cpu !== undefined ? `${stats.cpu.toFixed(1)}%` : '-',
          memory: stats && stats.memory !== undefined ? `${stats.memory.toFixed(1)}%` : '-',
          uptime: uptimeStr,
          lastSeen: edge.updateTime || '-',
          serialNumber: edge.devSerNum,
          configStatus,
          pendingCount: stats?.pending_count,
          driftCount: stats?.drift_count,
          lastHeartbeat: stats?.last_heartbeat,
        };
      });

      setDevices(mappedDevices);
    } catch (error) {
      console.error('Failed to fetch devices:', error);
    } finally {
      if (!isBackground) setLoading(false);
    }
  };

  useEffect(() => {
    fetchDevices(false);
  }, []);

  // Auto-refresh effect
  useEffect(() => {
    if (autoRefresh === 'off') return;

    const intervals = {
      '30s': 30000,
      '1m': 60000,
      '5m': 300000,
    };

    const interval = setInterval(() => fetchDevices(true), intervals[autoRefresh]);
    return () => clearInterval(interval);
  }, [autoRefresh]);

  // Filter devices by status
  const filteredDevices = statusFilter === 'All' 
    ? devices 
    : devices.filter(d => d.status === statusFilter);

  // Calculate metrics
  const metrics = {
    total: devices.length,
    online: devices.filter(d => d.status === 'Online').length,
    offline: devices.filter(d => d.status === 'Offline').length,
    warning: devices.filter(d => d.status === 'Warning').length,
    error: devices.filter(d => d.status === 'Error').length,
  };

  const columns: Column<DeviceWithTelemetry>[] = [
    {
      key: 'name',
      label: 'Device Name',
      sortable: true,
      render: (device) => (
        <div>
          <div className="font-medium text-gray-900">{device.name}</div>
          <div className="text-xs text-gray-500">{device.type}</div>
        </div>
      ),
    },
    {
      key: 'location',
      label: 'Location',
      sortable: true,
    },
    {
      key: 'status',
      label: 'Status',
      sortable: true,
      render: (device) => <StatusBadge status={device.status} />,
    },
    {
      key: 'cpu',
      label: 'CPU',
      sortable: true,
      render: (device) => {
        const cpuValue = device.cpu === '-' ? 0 : parseInt(device.cpu);
        const colorClass = cpuValue > 80 ? 'text-danger-600' : cpuValue > 60 ? 'text-warning-600' : 'text-success-600';
        return <span className={`font-medium ${colorClass}`}>{device.cpu}</span>;
      },
    },
    {
      key: 'memory',
      label: 'Memory',
      sortable: true,
      render: (device) => {
        const memValue = device.memory === '-' ? 0 : parseInt(device.memory);
        const colorClass = memValue > 80 ? 'text-danger-600' : memValue > 60 ? 'text-warning-600' : 'text-success-600';
        return <span className={`font-medium ${colorClass}`}>{device.memory}</span>;
      },
    },
    {
      key: 'uptime',
      label: 'Uptime',
      sortable: true,
    },
    {
      key: 'configStatus',
      label: 'Config Status',
      sortable: true,
      render: (device) => {
        if (device.configStatus === 'drift') {
          return (
            <span className="px-2 py-1 text-xs font-medium rounded-full bg-warning-100 text-warning-700">
              Drift ({device.driftCount})
            </span>
          );
        }
        if (device.configStatus === 'pending') {
          return (
            <span className="px-2 py-1 text-xs font-medium rounded-full bg-blue-100 text-blue-700">
              Pending ({device.pendingCount})
            </span>
          );
        }
        return (
          <span className="px-2 py-1 text-xs font-medium rounded-full bg-success-100 text-success-700">
            Synced
          </span>
        );
      },
    },
    {
      key: 'lastSeen',
      label: 'Last Seen',
      sortable: true,
      render: (device) => (
        <span className="text-sm text-gray-600">{device.lastSeen}</span>
      ),
    },
    {
      key: 'actions',
      label: 'Actions',
      render: (device) => (
        <div className="flex items-center space-x-2">
          <button
            className="btn-icon"
            title="View Details"
            onClick={() => navigate(`/edges/${device.serialNumber}`)}
          >
            <Eye className="w-4 h-4" />
          </button>
          <button
            className="btn-icon"
            title="Reboot"
            disabled={device.status === 'Offline'}
            onClick={() => navigate(`/edges/${device.serialNumber}/config`)}
          >
            <Power className="w-4 h-4" />
          </button>
          <button
            className="btn-icon"
            title="Diagnostics"
            disabled={device.status === 'Offline'}
            onClick={() => navigate(`/edges/${device.serialNumber}`)}
          >
            <Wrench className="w-4 h-4" />
          </button>
        </div>
      ),
    },
  ];

  return (
    <div className="space-y-6">
      {/* Header */}
      <div>
        <h1 className="text-3xl font-bold text-gray-900">Device Status</h1>
        <p className="text-gray-600 mt-1">Real-time monitoring of all network devices</p>
      </div>

      {/* Metrics Cards */}
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-5 gap-4">
        <StatCard icon={Monitor} title="Total Devices" value={metrics.total} color="primary" />
        <StatCard icon={CheckCircle} title="Online" value={metrics.online} color="success" />
        <StatCard icon={AlertTriangle} title="Warning" value={metrics.warning} color="warning" />
        <StatCard icon={XCircle} title="Error" value={metrics.error} color="danger" />
        <StatCard icon={ServerOff} title="Offline" value={metrics.offline} color="secondary" />
      </div>

      {/* Controls */}
      <div className="card">
        <div className="flex flex-wrap items-center justify-between gap-4">
          {/* Left: Filters */}
          <div className="flex items-center space-x-4">
            <div className="flex items-center space-x-2">
              <label className="text-sm font-medium text-gray-700">Status:</label>
              <select
                value={statusFilter}
                onChange={(e) => setStatusFilter(e.target.value as 'All' | StatusType)}
                className="input-base"
              >
                <option value="All">All</option>
                <option value="Online">Online</option>
                <option value="Offline">Offline</option>
                <option value="Warning">Warning</option>
                <option value="Error">Error</option>
              </select>
            </div>

            <div className="flex items-center space-x-2">
              <label className="text-sm font-medium text-gray-700">Auto-refresh:</label>
              <select
                value={autoRefresh}
                onChange={(e) => setAutoRefresh(e.target.value as typeof autoRefresh)}
                className="input-base"
              >
                <option value="off">Off</option>
                <option value="30s">30 seconds</option>
                <option value="1m">1 minute</option>
                <option value="5m">5 minutes</option>
              </select>
            </div>
          </div>

          {/* Right: Actions */}
          <div className="flex items-center space-x-2">
            {/* View Mode Toggle */}
            <div className="flex items-center bg-gray-100 rounded-lg p-1">
              <button
                onClick={() => setViewMode('table')}
                className={`px-3 py-1.5 rounded-md text-sm font-medium transition-colors ${
                  viewMode === 'table'
                    ? 'bg-white text-primary-600 shadow-sm'
                    : 'text-gray-600 hover:text-gray-900'
                }`}
              >
                <List className="w-4 h-4" />
              </button>
              <button
                onClick={() => setViewMode('grid')}
                className={`px-3 py-1.5 rounded-md text-sm font-medium transition-colors ${
                  viewMode === 'grid'
                    ? 'bg-white text-primary-600 shadow-sm'
                    : 'text-gray-600 hover:text-gray-900'
                }`}
              >
                <Grid3x3 className="w-4 h-4" />
              </button>
            </div>

            <button onClick={() => fetchDevices(false)} className="btn-secondary inline-flex items-center space-x-2">
              <RefreshCw className="w-4 h-4" />
              <span>Refresh</span>
            </button>

            <button className="btn-primary inline-flex items-center space-x-2">
              <Download className="w-4 h-4" />
              <span>Export</span>
            </button>
          </div>
        </div>
      </div>

      {/* Device List/Grid */}
      {viewMode === 'table' ? (
        <div className="card">
          <DataTable
            columns={columns}
            data={filteredDevices}
            loading={loading}
            searchable
            searchPlaceholder="Search devices..."
          />
        </div>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
          {filteredDevices.map((device) => (
            <div key={device.id} className="card hover:shadow-lg transition-shadow">
              <div className="flex items-start justify-between mb-3">
                <div>
                  <h3 className="font-semibold text-gray-900">{device.name}</h3>
                  <p className="text-xs text-gray-500">{device.type}</p>
                </div>
                <StatusBadge status={device.status} />
              </div>

              <div className="space-y-2 text-sm">
                <div className="flex justify-between">
                  <span className="text-gray-600">Location:</span>
                  <span className="font-medium text-gray-900">{device.location}</span>
                </div>
                <div className="flex justify-between">
                  <span className="text-gray-600">CPU:</span>
                  <span className={`font-medium ${
                    device.cpu === '-' ? 'text-gray-400' :
                    parseInt(device.cpu) > 80 ? 'text-danger-600' :
                    parseInt(device.cpu) > 60 ? 'text-warning-600' :
                    'text-success-600'
                  }`}>{device.cpu}</span>
                </div>
                <div className="flex justify-between">
                  <span className="text-gray-600">Memory:</span>
                  <span className={`font-medium ${
                    device.memory === '-' ? 'text-gray-400' :
                    parseInt(device.memory) > 80 ? 'text-danger-600' :
                    parseInt(device.memory) > 60 ? 'text-warning-600' :
                    'text-success-600'
                  }`}>{device.memory}</span>
                </div>
                <div className="flex justify-between">
                  <span className="text-gray-600">Uptime:</span>
                  <span className="font-medium text-gray-900">{device.uptime}</span>
                </div>
                <div className="flex justify-between">
                  <span className="text-gray-600">Last Seen:</span>
                  <span className="text-gray-600">{device.lastSeen}</span>
                </div>
              </div>

              <div className="flex items-center space-x-2 mt-4 pt-4 border-t border-gray-200">
                <button
                  className="btn-secondary flex-1 inline-flex items-center justify-center space-x-1 text-sm"
                  onClick={() => navigate(`/edges/${device.serialNumber}`)}
                >
                  <Eye className="w-4 h-4" />
                  <span>View</span>
                </button>
                <button
                  className="btn-icon"
                  title="Reboot"
                  disabled={device.status === 'Offline'}
                  onClick={() => navigate(`/edges/${device.serialNumber}/config`)}
                >
                  <Power className="w-4 h-4" />
                </button>
                <button
                  className="btn-icon"
                  title="Diagnostics"
                  disabled={device.status === 'Offline'}
                  onClick={() => navigate(`/edges/${device.serialNumber}`)}
                >
                  <Wrench className="w-4 h-4" />
                </button>
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
};

export default DeviceStatus;

